import { useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Plus } from 'lucide-react'

import type { PackagingBoxLogEntry } from '@/lib/api/packaging-box-log-api'
import { Button } from '@/components/ui/button'
import {
    HEADER_CELL,
    LEDGER_COLS,
    LEDGER_MIN_W,
} from '@/components/box-log/ledger-grid'
import { LogEntryEditRow } from '@/components/box-log/log-entry-edit-row'
import {
    LogEntryRow,
    takenBags,
    totalBoxes,
} from '@/components/box-log/log-entry-row'
import { useAuth } from '@/hooks/use-auth'
import { usePackagingBoxLogEntriesInfinite } from '@/hooks/use-packaging-box-log-query'
import { LedgerDayPager } from '@/components/common/ledger-day-pager'
import { useFetchAllPages } from '@/hooks/use-fetch-all-pages'
import { todayYMD } from '@/lib/date-presets'
import { formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/sales-warehouse/box-log/',
)({
    component: PackagingBoxLogPage,
})

function PackagingBoxLogPage() {
    const { hasPermission } = useAuth()
    const canWriteNotes = hasPermission?.('product_lots.write') ?? true
    const canWriteLog = hasPermission?.('packaging_logs.write') ?? true
    // Mở màn phiếu cần quyền xem kho; không có thì mã phiếu chỉ là chữ (M-61).
    const canOpenNotes = hasPermission?.('product_lots.read') ?? true

    // Sổ lật theo NGÀY như sổ giấy ngoài xưởng: mỗi trang là trọn một ngày.
    const [day, setDay] = useState(todayYMD)
    const query = usePackagingBoxLogEntriesInfinite({
        date_from: day,
        date_to: day,
    })
    const { data, isLoading } = query
    // Ngày cao điểm có thể vượt một trang API — kéo nốt cho đủ sổ, vì "còn
    // trang nữa" không phải thứ người ghi sổ phải bấm.
    useFetchAllPages(query)
    // Các trang đã tải nối lại thành một danh sách; server đã xếp ngày mới nhất
    // lên trước nên không sắp xếp lại ở đây.
    const entries = useMemo(
        () => (data?.pages ?? []).flatMap((p) => p.data ?? []),
        [data],
    )

    // Nhập ngay trong bảng: `adding` là dòng trống ở đầu, `editingId` là mẻ đang
    // được sửa tại chỗ. Hai thứ loại trừ nhau để không có hai dòng nhập cùng lúc.
    const [adding, setAdding] = useState(false)
    const [editingId, setEditingId] = useState<string | undefined>()

    const startAdd = () => {
        setEditingId(undefined)
        setAdding(true)
    }
    const startEdit = (entry: PackagingBoxLogEntry) => {
        setAdding(false)
        setEditingId(entry.id)
    }
    // `savedDay` là ngày của mẻ vừa lưu: dòng nhập giữ nguyên ngày lúc mở, nên
    // người ghi có thể lật sang trang khác rồi mới bấm Lưu. Không nhảy về đúng
    // ngày đó thì bảng không đổi gì sau khi Lưu và họ tưởng mất công, ghi lại
    // lần nữa thành mẻ trùng.
    const closeAll = () => {
        setAdding(false)
        setEditingId(undefined)
    }
    // Sau khi LƯU thì còn phải nhảy sang đúng ngày của mẻ vừa lưu: dòng nhập giữ
    // nguyên ngày lúc mở, nên người ghi có thể lật sang trang khác rồi mới bấm
    // Lưu. Không nhảy thì bảng không đổi gì và họ tưởng mất công, ghi lại lần
    // nữa thành mẻ trùng.
    //
    // Hàm RIÊNG chứ không thêm tham số cho `closeAll`: `onCancel={closeAll}` nối
    // thẳng vào onClick nên React truyền MouseEvent vào tham số đầu — nhận nhầm
    // nó làm ngày là vỡ cả trang.
    const doneAt = (savedDay?: string) => {
        if (typeof savedDay === 'string' && savedDay) setDay(savedDay)
        closeAll()
    }

    const waiting = entries.filter((e) => e.sync_state !== 'synced')
    const divergedCount = entries.filter(
        (e) => e.sync_state === 'diverged',
    ).length
    const totalBags = waiting.reduce((s, e) => s + takenBags(e), 0)
    const boxCount = waiting.reduce((s, e) => s + totalBoxes(e), 0)

    return (
        <div className="ledger-text space-y-4 page-pad">
            <header className="border-b-2 border-foreground pb-3">
                <h1 className="text-2xl font-bold md:text-3xl">
                    {m.box_log_title()}
                </h1>
                <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                    {m.box_log_subtitle()}
                </p>
            </header>

            <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2 border-b pb-3">
                <span className="flex items-baseline gap-2">
                    <b className="text-xl tabular-nums">{waiting.length}</b>
                    <span className="text-sm text-muted-foreground">
                        {m.box_log_stat_rows()}
                    </span>
                </span>
                <span className="flex items-baseline gap-2">
                    <b className="text-xl tabular-nums">
                        {formatQty(totalBags)}
                    </b>
                    <span className="text-sm text-muted-foreground">
                        {m.box_log_stat_bags()}
                    </span>
                </span>
                <span className="flex items-baseline gap-2">
                    <b className="text-xl tabular-nums">
                        {formatQty(boxCount)}
                    </b>
                    <span className="text-sm text-muted-foreground">
                        {m.box_log_stat_boxes()}
                    </span>
                </span>
                {divergedCount > 0 && (
                    <span className="rounded-full bg-destructive/15 px-3 py-1 text-sm font-semibold text-destructive">
                        {m.box_log_diverged_count({ n: divergedCount })}
                    </span>
                )}
                {/* Ba số trên chỉ cộng các mẻ ĐÃ TẢI, không phải toàn bộ sổ. */}
                <span className="text-xs text-muted-foreground">
                    {m.box_log_stat_scope({ n: entries.length })}
                </span>
            </div>

            {/* Nút thêm nằm TRÊN bảng: mẻ mới chèn vào đầu danh sách. */}
            <LedgerDayPager value={day} onChange={setDay} />

            {/* Nút thêm hiện ở MỌI ngày: đang mở trang nào cũng phải ghi được
                mẻ mới ngay, không phải lật về hôm nay trước. */}
            {canWriteLog && !adding && !editingId && (
                <Button
                    type="button"
                    variant="outline"
                    className="h-12"
                    onClick={startAdd}
                >
                    <Plus className="mr-1 size-4" />
                    {m.box_log_btn_add_row()}
                </Button>
            )}

            {/* Bảng sổ — kẻ ô như sổ giấy của xưởng. */}
            <section className="overflow-x-auto rounded-md border bg-card">
                <div className={LEDGER_MIN_W}>
                    <div
                        className={`grid gap-0 border-b bg-muted/40 text-xs font-semibold uppercase tracking-wide text-muted-foreground ${LEDGER_COLS}`}
                    >
                        <div className={HEADER_CELL}>
                            {m.box_log_col_date()}
                        </div>
                        <div className={HEADER_CELL}>
                            {m.box_log_col_source_lot()}
                        </div>
                        <div className={`${HEADER_CELL} text-right`}>
                            {m.box_log_col_bags()}
                        </div>
                        <div className={HEADER_CELL}>
                            {m.box_log_col_product()}
                        </div>
                        <div className={`${HEADER_CELL} text-right`}>
                            {m.box_log_col_qty()}
                        </div>
                        <div className={`${HEADER_CELL} text-right`}>
                            {m.box_log_col_bags_needed()}
                        </div>
                        <div className={`${HEADER_CELL} text-right`}>
                            {m.box_log_col_discount()}
                        </div>
                        <div className={HEADER_CELL}>
                            {m.box_log_col_status()}
                        </div>
                    </div>

                    {isLoading && (
                        <p className="p-6 text-center text-muted-foreground">
                            {m.box_log_loading()}
                        </p>
                    )}
                    {!isLoading && entries.length === 0 && !adding && (
                        <p className="p-6 text-center text-muted-foreground">
                            {m.box_log_empty()}
                        </p>
                    )}

                    {adding && (
                        <LogEntryEditRow
                            defaultDate={day}
                            onDone={doneAt}
                            onCancel={closeAll}
                        />
                    )}

                    {entries.map((entry) =>
                        editingId === entry.id ? (
                            <LogEntryEditRow
                                key={entry.id}
                                entry={entry}
                                onDone={doneAt}
                                onCancel={closeAll}
                            />
                        ) : (
                            <LogEntryRow
                                key={entry.id}
                                entry={entry}
                                canWriteNotes={canWriteNotes}
                                canWriteLog={canWriteLog}
                                canOpenNotes={canOpenNotes}
                                onEdit={startEdit}
                            />
                        ),
                    )}
                </div>
            </section>
        </div>
    )
}
